import { useEffect, useRef, useCallback, useState } from 'react';
import { io } from 'socket.io-client';
import { toast } from 'react-hot-toast';
import API_CONFIG from '../config/api';

const useWebSocket = (token) => {
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [jobs, setJobs] = useState([]); // Активные задачи пользователя
  const [queueStats, setQueueStats] = useState(null);

  // Инициализация соединения
  useEffect(() => {
    if (!token) return;

    // Используем базовый URL из API конфигурации (без /api суффикса для WebSocket)
    const baseUrl = API_CONFIG.getBaseURL();
    const socketUrl = baseUrl.replace('/api', ''); // Убираем /api для WebSocket
    
    const socket = io(socketUrl, {
      auth: {
        token: token
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });

    socketRef.current = socket;

    // Обработчики событий подключения
    socket.on('connect', () => {
      // console.log('🔌 WebSocket подключен');
      setIsConnected(true);
      // Запрашиваем текущие задачи пользователя
      socket.emit('get-user-jobs');
      socket.emit('get-queue-stats');
    });

    socket.on('disconnect', () => {
      // console.log('🔌 WebSocket отключен');
      setIsConnected(false);
    });

    socket.on('connect_error', (error) => {
      console.error('❌ Ошибка подключения WebSocket:', error);
      setIsConnected(false);
    });

    // Обработчики событий задач
    socket.on('job-created', (jobData) => {
      // console.log('✅ Новая задача создана:', jobData);
      
      // Обновляем существующий файл если он уже есть в списке (оптимистичные записи)
      setJobs(prev => {
        const existingFileIndex = prev.findIndex(job => 
          job.fileName === jobData.fileName && job.jobId === null
        );
        
        if (existingFileIndex !== -1) {
          // Обновляем существующий файл
          const updated = [...prev];
          updated[existingFileIndex] = {
            ...updated[existingFileIndex],
            jobId: jobData.jobId,
            chatId: jobData.chatId || (jobData.data ? jobData.data.chatId : undefined),
            status: 'queued',
            progress: 0,
            stage: 'В очереди'
          };
          return updated;
        } else {
          // Добавляем новый файл
          return [...prev, { 
            ...jobData, 
            chatId: jobData.chatId || (jobData.data ? jobData.data.chatId : undefined),
            status: 'queued', 
            progress: 0, 
            stage: 'В очереди' 
          }];
        }
      });
    });

    socket.on('job-started', (jobData) => {
      // console.log('🚀 Задача начата:', jobData);
      setJobs(prev => prev.map(job => {
        // Ищем по jobId или по имени файла
        if ((job.jobId === jobData.jobId) || (job.fileName === jobData.fileName && job.jobId === null)) {
          return { 
            ...job, 
            status: 'active', 
            progress: 0, 
            stage: 'Начало обработки файла',
            jobId: jobData.jobId, // Обновляем/уточняем jobId
            chatId: job.chatId || jobData.chatId || (jobData.data ? jobData.data.chatId : undefined)
          };
        }
        return job;
      }));
    });

    socket.on('job-progress', (progressData) => {
      // console.log('📊 Прогресс задачи:', progressData);
      setJobs(prev => prev.map(job => {
        // Ищем по jobId или по имени файла
        if ((job.jobId === progressData.jobId) || (job.fileName === progressData.fileName && job.jobId === null)) {
          const prog = progressData.progress;
          const percent = typeof prog === 'object' ? prog.percent : prog;
          // Сообщение может быть в отдельном поле или внутри объекта прогресса
          const messageFromProg = typeof prog === 'object' ? prog.message : undefined;
          const message = progressData.message || messageFromProg;
          const updated = { 
            ...job, 
            progress: percent,
            jobId: progressData.jobId || job.jobId, // Обновляем jobId если он был null
            chatId: job.chatId || (progressData.data ? progressData.data.chatId : undefined)
          };
          if (message) {
            updated.stage = message;
          }
          return updated;
        }
        return job;
      }));
    });

    socket.on('job-completed', (resultData) => {
      // console.log('✅ Задача завершена:', resultData);
      setJobs(prev => prev.map(job => {
        // Ищем по jobId или по имени файла
        if ((job.jobId === resultData.jobId) || (job.fileName === resultData.fileName && job.jobId === null)) {
          return { 
            ...job, 
            status: 'completed', 
            progress: 100, 
            result: resultData.result,
            jobId: resultData.jobId || job.jobId, // Обновляем jobId если он был null
            chatId: job.chatId || (resultData.data ? resultData.data.chatId : undefined)
          };
        }
        return job;
      }));
      
      // Удаляем завершенную задачу через 5 секунд
      setTimeout(() => {
        setJobs(prev => prev.filter(job => job.jobId !== resultData.jobId));
      }, 5000);
    });

    socket.on('job-failed', (errorData) => {
      // console.error('❌ Задача провалена:', errorData);
      setJobs(prev => prev.map(job => {
        // Ищем по jobId или по имени файла
        const fileName = errorData.data?.fileName || errorData.fileName;
        if ((job.jobId === errorData.jobId) || (job.fileName === fileName && job.jobId === null)) {
          return { 
            ...job, 
            status: 'failed', 
            error: errorData.error, 
            stage: `Ошибка: ${errorData.error}`,
            jobId: errorData.jobId || job.jobId, // Обновляем jobId если он был null
            chatId: job.chatId || (errorData.data ? errorData.data.chatId : undefined)
          };
        }
        return job;
      }));
      const fileName = errorData.data?.fileName || errorData.fileName || '';
      toast.error(fileName ? `Ошибка обработки файла "${fileName}": ${errorData.error}` : `Ошибка обработки: ${errorData.error}`);
    });

    // Обработчик события file-failed (отправляется из QueueManager)
    socket.on('file-failed', (errorData) => {
      // console.error('❌ Файл не обработан:', errorData);
      setJobs(prev => prev.map(job => {
        // Ищем по имени файла, так как jobId может быть null в оптимистичных записях
        if (job.fileName === errorData.fileName) {
          return { 
            ...job, 
            status: 'failed', 
            error: errorData.error, 
            stage: `Ошибка: ${errorData.error}`,
            jobId: errorData.jobId, // Обновляем jobId если он был null
            chatId: job.chatId || (errorData.data ? errorData.data.chatId : undefined)
          };
        }
        return job;
      }));
      toast.error(`Ошибка обработки файла "${errorData.fileName}": ${errorData.error}`);
    });

    // Обработчик события file-processed (отправляется из QueueManager)
    socket.on('file-processed', (resultData) => {
      // console.log('✅ Файл обработан:', resultData);
      setJobs(prev => prev.map(job => {
        // Ищем по имени файла, так как jobId может быть null в оптимистичных записях
        if (job.fileName === resultData.fileName) {
          return { 
            ...job, 
            status: 'completed', 
            progress: 100, 
            result: resultData.result, 
            stage: 'Обработка завершена',
            jobId: resultData.jobId, // Обновляем jobId если он был null
            chatId: job.chatId || (resultData.data ? resultData.data.chatId : undefined)
          };
        }
        return job;
      }));
      toast.success(`Файл "${resultData.fileName}" успешно обработан!`);
      
      // Удаляем завершенную задачу через 5 секунд
      setTimeout(() => {
        setJobs(prev => prev.filter(job => job.fileName !== resultData.fileName));
      }, 5000);
    });

    socket.on('job-cancelled', (cancelData) => {
      // console.log('🚫 Задача отменена:', cancelData);
      setJobs(prev => prev.filter(job => 
        !(job.jobId === cancelData.jobId && job.queueName === cancelData.queueName)
      ));
    });

    socket.on('user-jobs', (userJobs) => {
      //console.log('📋 Задачи пользователя:', userJobs);
      // Форматируем задачи для отображения прогресса
      const formattedJobs = userJobs
        .filter(job => job.state !== 'completed' && job.state !== 'failed')
        .map(job => ({
          jobId: job.id,
          queue: job.queue,
          fileName: job.data.fileName,
          fileSize: job.data.fileSize,
          status: job.state === 'waiting' ? 'queued' : job.state,
          progress: job.progress,
          stage: job.state === 'waiting' ? 'В очереди' : 'Начало обработки файла',
          chatId: job.data?.chatId
        }));
      setJobs(formattedJobs);
    });

    socket.on('queue-status', (statusData) => {
      setQueueStats(statusData.stats);
    });

    // Очистка при размонтировании
    return () => {
      socket.disconnect();
    };
  }, [token]);

  // Функция отмены задачи
  const cancelJob = useCallback((jobId, queueName) => {
    if (socketRef.current && isConnected) {
      console.log(`🚫 Отменяем задачу ${jobId} в очереди ${queueName}`);
      socketRef.current.emit('cancel-job', { jobId, queueName });
    }
  }, [isConnected]);

  // Функция подписки на задачу
  const subscribeToJob = useCallback((jobId, queueName) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('subscribe-to-job', { jobId, queueName });
    }
  }, [isConnected]);

  // Функция обновления задач
  const refreshJobs = useCallback(() => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('get-user-jobs');
    }
  }, [isConnected]);

  return {
    isConnected,
    jobs,
    queueStats,
    cancelJob,
    subscribeToJob,
    refreshJobs,
    socketRef
  };
};

export default useWebSocket; 